import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Button } from "../components/ui/Button";
import { ConfirmDialog } from "../components/ui/Modal";
import { SearchInput } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { Post } from "../api/types";

interface PostsModerationViewProps {
  title: string;
  mediaType?: "video" | "image";
  emptyTitle: string;
}

/**
 * Powers both Posts & Moments and Shorts/Reels — same table, same route,
 * same moderation actions. Shorts are ordinary posts with
 * media_type='video' (see the Android app's ShortsRepository), not a
 * separate feature with its own data model, so filtering this one view is
 * the real, correct implementation rather than a placeholder standing in
 * for a feature that doesn't exist yet.
 */
export function PostsModerationView({ title, mediaType, emptyTitle }: PostsModerationViewProps) {
  const [search, setSearch] = useState("");
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<Post>("/admin/posts", {
    mediaType,
    search: search || undefined,
  });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [pendingDelete, setPendingDelete] = useState<Post | null>(null);
  const [pendingBulkDelete, setPendingBulkDelete] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  function toggleSelected(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) => (prev.size === rows.length ? new Set() : new Set(rows.map((r) => r.id))));
  }

  async function confirmDelete() {
    if (!pendingDelete) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/posts/${pendingDelete.id}`, { method: "DELETE" });
      showToast("Deleted", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete", "error");
    } finally {
      setIsSubmitting(false);
      setPendingDelete(null);
    }
  }

  async function confirmBulkDelete() {
    setIsSubmitting(true);
    try {
      await apiRequest("/admin/posts/bulk-delete", { method: "DELETE", body: { postIds: Array.from(selected) } });
      showToast(`${selected.size} deleted`, "success");
      setSelected(new Set());
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not delete selection", "error");
    } finally {
      setIsSubmitting(false);
      setPendingBulkDelete(false);
    }
  }

  const columns: Column<Post>[] = [
    {
      header: "",
      className: "w-10",
      render: (row) => (
        <input type="checkbox" checked={selected.has(row.id)} onChange={() => toggleSelected(row.id)} className="h-4 w-4 rounded border-surface-border" />
      ),
    },
    {
      header: "Post",
      render: (row) => (
        <div className="flex items-center gap-3">
          {row.media_url ? (
            mediaType === "video" ? (
              <video src={row.media_url} className="h-10 w-10 rounded-lg object-cover" muted />
            ) : (
              <img src={row.media_url} className="h-10 w-10 rounded-lg object-cover" alt="" />
            )
          ) : (
            <div className="h-10 w-10 rounded-lg bg-surface-raised" />
          )}
          <p className="max-w-xs truncate text-ink">{row.caption || <span className="text-ink-faint">No caption</span>}</p>
        </div>
      ),
    },
    { header: "Author", render: (row) => row.author_username },
    { header: "Posted", render: (row) => new Date(row.created_at).toLocaleDateString() },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <Button size="sm" variant="danger" onClick={() => setPendingDelete(row)}>
          Delete
        </Button>
      ),
    },
  ];

  return (
    <AppShell title={title}>
      <div className="mb-4 flex items-center justify-between">
        <SearchInput value={search} onChange={setSearch} placeholder="Search captions..." />
        {selected.size > 0 && (
          <div className="flex items-center gap-2 rounded-lg border border-surface-border bg-surface-card px-3 py-2">
            <span className="text-xs text-ink-muted">{selected.size} selected</span>
            <Button size="sm" variant="danger" onClick={() => setPendingBulkDelete(true)}>
              Bulk Delete
            </Button>
          </div>
        )}
      </div>

      {rows.length > 0 && (
        <div className="mb-2 flex items-center gap-2 px-1">
          <input type="checkbox" checked={selected.size === rows.length} onChange={toggleSelectAll} className="h-4 w-4 rounded border-surface-border" />
          <span className="text-xs text-ink-faint">Select all on this page</span>
        </div>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle={emptyTitle}
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={confirmDelete}
        isLoading={isSubmitting}
        isDangerous
        title="Delete this post?"
        description="This permanently removes the post for all users. This action is logged and cannot be undone."
        confirmLabel="Delete"
      />

      <ConfirmDialog
        isOpen={pendingBulkDelete}
        onClose={() => setPendingBulkDelete(false)}
        onConfirm={confirmBulkDelete}
        isLoading={isSubmitting}
        isDangerous
        title={`Delete ${selected.size} posts?`}
        description="This permanently removes all selected posts. This action is logged and cannot be undone."
        confirmLabel="Delete All"
      />
    </AppShell>
  );
}
